/**
 * Editorial content for the subscription pages and the landing page's
 * subscription section: metric guides, explainer cards and FAQs. No market
 * figures live here; those come from GET /api/market/subscription.
 */

export const SUBSCRIPTION_METRICS = [
  {
    title: 'Closing Date & Cut-Off',
    body: 'The last day investors can place bids. Exchange bidding closes at 5:00 PM IST, and UPI mandates must be approved in your banking app by the same 5:00 PM cut-off on the closing day.',
  },
  {
    title: 'Total Issue Amount',
    body: 'The gross value of all shares offered to the public (fresh issue plus offer for sale), calculated at the upper end of the price band.',
  },
  {
    title: 'QIB (Qualified Institutional)',
    body: 'How many times the QIB portion is subscribed, excluding anchor investors who bid a day before opening. Mutual funds, FIIs and banks usually bid heavily in the last hours of day 3.',
  },
  {
    title: 'sNII & bNII Breakdown',
    body: 'sNII (small NII) covers bids between ₹2 lakh and ₹10 lakh, one-third of the NII quota. bNII (big NII) covers bids above ₹10 lakh, the other two-thirds.',
  },
  {
    title: 'Retail Category (RII)',
    body: 'How many times the retail portion (bids up to ₹2,00,000) is subscribed. When retail is oversubscribed, allotment is decided by a lottery under SEBI rules.',
  },
  {
    title: 'Employee & Shareholder',
    body: 'Reserved portions, often at a discount, for permanent employees and shareholders of the parent company. Each has its own subscription multiple.',
  },
];

export const SUBSCRIPTION_FAQS = [
  {
    question: 'Does Alambrix IPO show consolidated live IPO subscription status from both BSE and NSE?',
    answer:
      'Yes. The figures combine bids placed on both the Bombay Stock Exchange (BSE) and the National Stock Exchange (NSE), so you see one unified demand number for every investor category. They refresh every few minutes during the bidding window (10:00 AM to 5:00 PM IST).',
  },
  {
    question: 'How to calculate or measure IPO oversubscription or undersubscription?',
    answer:
      'Divide the number of shares bid for by the number of shares offered in that category. Above 1x means the category is oversubscribed, below 1x undersubscribed. The total multiple compares all bids with all shares on offer, excluding the anchor portion.',
  },
  {
    question: 'What is the difference between sNII and bNII quotas in subscription tracking?',
    answer:
      'Both belong to the non-institutional (NII/HNI) category. sNII is for bids of ₹2–10 lakh and gets one-third of the NII quota; bNII is for bids above ₹10 lakh and gets two-thirds. The NII column shows the combined multiple.',
  },
  {
    question: 'When does IPO bidding close and what is the UPI mandate cut-off timing?',
    answer:
      'Exchange bidding closes at 5:00 PM IST on the closing day, although some brokers stop accepting bids earlier. UPI mandates must be approved by 5:00 PM IST the same day, or the bid is rejected.',
  },
];

// SME Subscription Status page (/sme-ipo-subscription)
export const SME_SUBSCRIPTION_METRICS = [
  {
    title: 'Closing Date & Cut-Off',
    body: 'SME bidding follows the same timetable as mainboard issues: the exchange stops accepting bids at 5:00 PM IST on the closing day, and UPI mandates must be approved by that same cut-off.',
  },
  {
    title: 'Lot-Based Bidding',
    body: 'SME bids are placed in whole lots, and each lot is a large amount. That is why an SME issue can be subscribed many times over with far fewer applications than a mainboard IPO.',
  },
  {
    title: 'QIB (Qualified Institutional)',
    body: 'How many times the institutional portion is subscribed, excluding any anchor allocation made before the issue opened. Many SME issues keep this portion small.',
  },
  {
    title: 'NII / HNI Category',
    body: 'Bids from non-institutional investors applying above the individual-investor limit. Where the exchange splits the category, the sNII and bNII columns appear alongside the combined NII multiple.',
  },
  {
    title: 'Individual Investor (Retail)',
    body: 'How many times the individual-investor portion is subscribed. When it is oversubscribed, allotment is decided by a draw of lots, so a high multiple sharply lowers your chance of an allotment.',
  },
  {
    title: 'Market Maker Portion',
    body: 'Every SME issue reserves a slice for its designated market maker, who quotes buy and sell prices after listing. That reservation is not part of the public subscription multiple.',
  },
];

export const SME_SUBSCRIPTION_FAQS = [
  {
    question: 'Where does the SME IPO subscription data on this page come from?',
    answer:
      "From the exchange bidding feed for SME issues on NSE Emerge. The category-wise figures refresh throughout the bidding window (10:00 AM to 5:00 PM IST), and SME issues that haven't opened yet are listed with their bidding dates.",
  },
  {
    question: 'How is SME IPO oversubscription calculated?',
    answer:
      'Divide the shares bid for by the shares offered in that category. Above 1x means the category is oversubscribed, below 1x undersubscribed. The total multiple compares all bids with all shares on offer, excluding the anchor and market maker portions.',
  },
  {
    question: 'Why can an SME IPO show a huge subscription multiple with few applications?',
    answer:
      'SME shares are bid for in large lots, and the issue itself is small. A modest number of applications can therefore cover the shares on offer many times over.',
  },
  {
    question: 'Does a high SME subscription mean a strong listing?',
    answer:
      'Not necessarily. Demand shows interest during bidding, but SME stocks trade in lots with thin post-listing volumes, so prices can move sharply either way. Read the RHP and weigh the risks before applying.',
  },
];

// Explainer cards under the landing page's Live IPO Subscription Demand section
export const SUBSCRIPTION_INSIGHTS = [
  {
    id: 'qib',
    title: 'What is the QIB Quota?',
    desc: 'Qualified Institutional Buyers (mutual funds, FPIs, banks and insurers) get up to 50% of a book-built issue. Much of their bidding typically arrives on the last day.',
    tag: 'Institutional Rules',
  },
  {
    id: 'nii',
    title: 'sNII vs bNII Explained',
    desc: 'sNII covers non-institutional bids of ₹2 lakh to ₹10 lakh (small HNI quota); bNII covers bids above ₹10 lakh (big HNI quota). One-third of the NII portion goes to sNII.',
    tag: 'Allotment Math',
  },
  {
    id: 'timings',
    title: 'Bidding Timings & Cut-Off',
    desc: 'Bidding closes at 5:00 PM IST on the final issue day, and UPI mandates must be approved by 5:00 PM that same day under the T+3 timeline.',
    tag: 'UPI Mandate Sync',
  },
];
